/* ==========================================================================
   App Component
   Task F19/F20/F21: Updated routing with slug-based paths, admin auth, and admin layout
   Task F26: Added ErrorBoundary for unhandled errors
   Task F33: Added pricing, contact-sales, checkout, and internal routes
   ========================================================================== */

import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ToastProvider } from '@/context/ToastContext'
import { ToastContainer } from '@/components/ui/ToastContainer'
import { AuthProvider } from '@/context/AuthContext'
import { PortalProvider, usePortalSlug } from '@/context/PortalContext'
import { AdminAuthProvider } from '@/context/AdminAuthContext'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { RequireProfile } from '@/components/auth/RequireProfile'
import { AdminProtectedRoute } from '@/components/auth/AdminProtectedRoute'
import { InternalProtectedRoute } from '@/components/auth/InternalProtectedRoute'
import { PortalLayout } from '@/components/layout/PortalLayout'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { InternalLayout } from '@/components/layout/InternalLayout'
import { ThemeProvider } from '@/components/layout/ThemeProvider'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SessionExpiredModal } from '@/components/auth/SessionExpiredModal'
import { AdminSessionExpiredModal } from '@/components/auth/AdminSessionExpiredModal'

// Pages
import {
  Login,
  ChangePassword,
  ForgotPassword,
  AcceptInvitation,
  ResetPassword,
  PrescriptionForm,
  PrescriptionHistory,
  SavedScripts,
  Profile,
  Confirmation,
  BulkConfirmation,
  NotFound,
  Landing,
  ContactSales,
  Changelog,
  CheckoutSuccess,
  CheckoutCancel,
  ComponentDemo,
  AdminSignup,
  AdminLogin,
  AdminAcceptInvitation,
  AdminForgotPassword,
  AdminResetPassword,
  AdminLoginRedirect,
  AdminDashboard,
  AdminSettings,
  AdminCatalog,
  AdminCategories,
  AdminPricing,
  AdminGeneralCustomFields,
  AdminProviders,
  AdminProviderDetail,
  AdminProviderSettings,
  AdminAccessRequests,
  AdminFaxes,
  AdminBilling,
  AdminTeam,
  AdminUserGuide,
  AdminAccount,
  ProviderLanding,
  ProviderUserGuide,
  ProviderPharmacyNotes,
  ProviderCatalog,
  IntakeForm,
  InternalLogin,
  GenerateCheckoutLink,
  IntakeList,
  IntakeDetail,
  Metrics,
  InternalSecurity,
} from '@/pages'

/**
 * Main application component.
 * Sets up providers and routing for the entire app.
 *
 * Route structure (Task F19/F20):
 * Global routes (no slug):
 * - / - Landing/marketing page with pricing (public)
 * - /admin/signup - Redirects to /contact-sales (Task F33)
 * - /admin/login - Admin login redirect (prompts for pharmacy slug)
 * - /contact-sales - Sales scheduling page (public, Task F33)
 * - /checkout/success - Stripe checkout success return (public, Task F33)
 * - /checkout/cancel - Stripe checkout cancel return (public, Task F33)
 * - /internal/login - Super-admin login (public, Task F33)
 * - /internal/checkout - Checkout link generator (protected, Task F33)
 *
 * Portal routes (/rx/:slug/*):
 * - /rx/:slug/login - Provider login (public)
 * - /rx/:slug/forgot-password - Password reset request (public)
 * - /rx/:slug/accept-invitation - Initial password setup from invite (public)
 * - /rx/:slug/reset-password - Password reset from email (public)
 * - /rx/:slug - Provider landing page (public)
 * - /rx/:slug/prescribe - Prescription form (protected, requires complete profile)
 * - /rx/:slug/profile - Provider profile (protected)
 * - /rx/:slug/confirmation/:id - Submission confirmation (protected)
 *
 * Admin routes (/rx/:slug/admin/*):
 * - /rx/:slug/admin/login - Admin login (public, Task F20)
 * - /rx/:slug/admin/accept-invitation - Admin initial password setup from invite (public)
 * - /rx/:slug/admin/forgot-password - Admin password reset request (public)
 * - /rx/:slug/admin/reset-password - Admin password reset from email (public)
 * - /rx/:slug/admin - Dashboard (protected, Task F31)
 * - /rx/:slug/admin/dashboard - Dashboard (protected, Task F31)
 * - /rx/:slug/admin/settings - Portal settings (protected, Task F22)
 * - /rx/:slug/admin/catalog - Product catalog (protected, Task F23)
 * - /rx/:slug/admin/providers - Provider management (protected, Task F24)
 * - /rx/:slug/admin/providers/:providerId - Provider details (protected, PORTAL-74)
 *
 * Development routes:
 * - /demo - Component library demo
 */
function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider>
          <Routes>
          {/* Global routes (no slug) */}
          <Route path="/" element={<Landing />} />

          {/* Admin signup (Task F19b) */}
          <Route path="/admin/signup" element={<AdminSignup />} />

          {/* Admin login redirect (prompts for pharmacy slug) */}
          <Route path="/admin/login" element={<AdminLoginRedirect />} />

          {/* Contact Sales (Task F33) */}
          <Route path="/contact-sales" element={<ContactSales />} />

          {/* Changelog */}
          <Route path="/changelog" element={<Changelog />} />

          {/* Checkout return pages (Task F33) */}
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/checkout/canceled" element={<CheckoutCancel />} />

          {/* Intake form (Task F34) — before /:slug catch-all */}
          <Route path="/intake/:token" element={<IntakeForm />} />

          {/* Internal tools (Task F33/F36) */}
          <Route path="/internal/login" element={<InternalLogin />} />
          <Route
            path="/internal"
            element={
              <InternalProtectedRoute>
                <InternalLayout />
              </InternalProtectedRoute>
            }
          >
            <Route path="checkout" element={<GenerateCheckoutLink />} />
            <Route path="intake" element={<IntakeList />} />
            <Route path="intake/:id" element={<IntakeDetail />} />
            <Route path="metrics" element={<Metrics />} />
            <Route path="security" element={<InternalSecurity />} />
            <Route index element={<IntakeList />} />
          </Route>

          {/* Development routes */}
          {import.meta.env.DEV && (
            <Route path="/demo" element={<ComponentDemo />} />
          )}

          {/* Portal routes - all routes under /rx/:slug/* */}
          <Route path="/rx/:slug" element={<PortalLayoutWrapper />}>
            {/* Public auth routes */}
            <Route path="login" element={<Login />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="accept-invitation" element={<AcceptInvitation />} />
            <Route path="reset-password" element={<ResetPassword />} />

            {/* Provider landing page (public, no auth) */}
            <Route index element={<ProviderLanding />} />

            {/* Forced first-login password change (PORTAL-75). Protected, but
                NOT wrapped in RequireProfile so a flagged provider can reach it;
                the path-guard in ProtectedRoute exempts this route from looping. */}
            <Route
              path="change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />

            {/* Protected provider routes */}
            <Route
              path="prescribe"
              element={
                <ProtectedRoute>
                  <RequireProfile>
                    <PrescriptionForm />
                  </RequireProfile>
                </ProtectedRoute>
              }
            />
            <Route
              path="saved"
              element={
                <ProtectedRoute>
                  <RequireProfile>
                    <SavedScripts />
                  </RequireProfile>
                </ProtectedRoute>
              }
            />
            <Route
              path="history"
              element={
                <ProtectedRoute>
                  <RequireProfile>
                    <PrescriptionHistory />
                  </RequireProfile>
                </ProtectedRoute>
              }
            />
            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="confirmation/bulk"
              element={
                <ProtectedRoute>
                  <BulkConfirmation />
                </ProtectedRoute>
              }
            />
            <Route
              path="confirmation/:id"
              element={
                <ProtectedRoute>
                  <Confirmation />
                </ProtectedRoute>
              }
            />
            <Route
              path="guide"
              element={
                <ProtectedRoute>
                  <ProviderUserGuide />
                </ProtectedRoute>
              }
            />
            {/* Pharmacy-wide notes (PORTAL-159). Like `guide`, this is not
                behind RequireProfile — a provider still completing their
                profile should be able to read their pharmacy's rules. */}
            <Route
              path="notes"
              element={
                <ProtectedRoute>
                  <ProviderPharmacyNotes />
                </ProtectedRoute>
              }
            />
            {/* Read-only catalog browse (PORTAL-116). Like `notes`, this is
                not behind RequireProfile — the catalog is reference material
                a provider can read before completing their profile. */}
            <Route
              path="catalog"
              element={
                <ProtectedRoute>
                  <ProviderCatalog />
                </ProtectedRoute>
              }
            />

            {/* Admin routes (Task F20) */}
            <Route path="admin" element={<AdminLayoutWrapper />}>
              {/* Public admin auth routes */}
              <Route path="login" element={<AdminLogin />} />
              <Route path="accept-invitation" element={<AdminAcceptInvitation />} />
              <Route path="forgot-password" element={<AdminForgotPassword />} />
              <Route path="reset-password" element={<AdminResetPassword />} />

              {/* Protected admin routes */}
              <Route
                index
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="dashboard"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <AdminProtectedRoute requireManager>
                    <AdminSettings />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="catalog"
                element={
                  <AdminProtectedRoute>
                    <AdminCatalog />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="categories"
                element={
                  <AdminProtectedRoute requireManager>
                    <AdminCategories />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="pricing"
                element={
                  <AdminProtectedRoute>
                    <AdminPricing />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="general-custom-fields"
                element={
                  <AdminProtectedRoute requireManager>
                    <AdminGeneralCustomFields />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="providers"
                element={
                  <AdminProtectedRoute>
                    <AdminProviders />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="providers/:providerId"
                element={
                  <AdminProtectedRoute>
                    <AdminProviderDetail />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="providers/:providerId/settings"
                element={
                  <AdminProtectedRoute>
                    <AdminProviderSettings />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="access-requests"
                element={
                  <AdminProtectedRoute>
                    <AdminAccessRequests />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="faxes"
                element={
                  <AdminProtectedRoute requireManager>
                    <AdminFaxes />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="team"
                element={
                  <AdminProtectedRoute requireManager>
                    <AdminTeam />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="billing"
                element={
                  <AdminProtectedRoute requireManager>
                    <AdminBilling />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="guide"
                element={
                  <AdminProtectedRoute>
                    <AdminUserGuide />
                  </AdminProtectedRoute>
                }
              />
              {/* Own security settings (2FA, PORTAL-187) — every role */}
              <Route
                path="account"
                element={
                  <AdminProtectedRoute>
                    <AdminAccount />
                  </AdminProtectedRoute>
                }
              />

              {/* Admin 404 */}
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Portal 404 - invalid path within valid slug */}
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Global 404 - matches everything else */}
          <Route path="*" element={<NotFound />} />
          </Routes>
          <ToastContainer />
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

/**
 * Wrapper that provides PortalProvider, ThemeProvider, and AuthProvider
 * for all portal routes (/:slug/*).
 * ThemeProvider injects pharmacy-specific CSS variables for white-labeling.
 * PortalLayout validates the slug and renders Outlet.
 * Task F26: Added SessionExpiredModal for graceful session expiry handling.
 */
function PortalLayoutWrapper() {
  return (
    <PortalProvider>
      <ThemeProvider>
        <AuthProvider>
          <PortalLayout />
          <SessionExpiredModal />
        </AuthProvider>
      </ThemeProvider>
    </PortalProvider>
  )
}

/**
 * Wrapper that provides AdminAuthProvider and AdminLayout for admin routes.
 * Must be used within PortalProvider to access the slug.
 * Task F21: Uses AdminLayout with header, sidebar, and content area.
 * Task F26: Added AdminSessionExpiredModal for graceful session expiry handling.
 */
function AdminLayoutWrapper() {
  const slug = usePortalSlug()

  return (
    <AdminAuthProvider pharmacySlug={slug}>
      <AdminLayout />
      <AdminSessionExpiredModal />
    </AdminAuthProvider>
  )
}

export default App
